import React, { useEffect, useRef } from 'react';
import { claimAudioChannel, releaseAudioChannel } from '../utils/audioChannel';

export default function DeployTroopsAnimation({ sourceCountry, targetCountry, onComplete }) {
  const videoRef = useRef(null);
  const soundsRef = useRef([]);
  const [selectedVideo, setSelectedVideo] = React.useState('');

  useEffect(() => {
    // 🔈 Claim audio channel so deploy SFX play over iOS
    claimAudioChannel();
    // RANDOM: Pick one of the SIX deploy troops videos
    const videos = [
      '/assets/deploy-troops-video.mp4',
      '/assets/deploy-troops-video-2.mp4',
      '/assets/deploy-troops-video-3-bombing.mp4',
      '/assets/deploy-troops-video-4-chopper.mp4',
      '/assets/deploy-troops-video-5-carrier.mp4',  // Aircraft Carrier
      '/assets/carrier-takeoff.mov'  // NEW: Gorgeous carrier takeoff (longer)
    ];
    const randomVideo = videos[Math.floor(Math.random() * videos.length)];
    setSelectedVideo(randomVideo);
    console.log(`🎬 Playing random deploy video: ${randomVideo}`);

    // RANDOM: Pick ONE of these two sounds
    const randomSounds = [
      '/assets/audio/deploy-sound-1.mp3',
      '/assets/audio/deploy-sound-2.mp3'
    ];
    const selectedRandom = randomSounds[Math.floor(Math.random() * randomSounds.length)];
    
    // LAYERED: Always play these 3 together
    const layeredSounds = [
      '/assets/audio/deploy-sound-3.mp3',
      '/assets/audio/deploy-music-1.mp3',
      '/assets/audio/deploy-music-2.mp3'
    ];

    // Play the random selected sound
    const randomSound = new Audio(selectedRandom);
    randomSound.volume = 0.7;
    soundsRef.current.push(randomSound);
    randomSound.play().catch(e => console.log('Random deploy sound failed:', e));
    console.log(`🎵 Playing random deploy sound: ${selectedRandom}`);

    // Play all 3 layered sounds
    layeredSounds.forEach((soundPath, index) => {
      const sound = new Audio(soundPath);
      sound.volume = 0.6;
      soundsRef.current.push(sound);
      sound.play().catch(e => console.log(`Layered deploy sound ${index + 1} failed:`, e));
    });
    console.log('🎵 Playing 3 layered deploy sounds');

    // Auto-complete after 6 seconds (video duration)
    const timer = setTimeout(() => {
      onComplete();
    }, 6000);

    // Cleanup
    return () => {
      clearTimeout(timer);
      soundsRef.current.forEach(sound => sound.pause());
      // 🔈 Release audio channel
      releaseAudioChannel();
    };
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
      {/* Video Background - Random selection */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        playsInline
        loop={false}
        key={selectedVideo} // Force re-render when video changes
      >
        {selectedVideo && <source src={selectedVideo} type="video/mp4" />}
      </video>

      {/* Dark overlay for text visibility */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Text Overlay */}
      <div className="relative z-10 text-center">
        <div className="mb-4">
          <h2 
            className="text-4xl md:text-5xl font-black uppercase tracking-wider mb-2"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              textShadow: `
                0 0 20px rgba(0, 255, 0, 0.8),
                0 0 40px rgba(34, 197, 94, 0.6),
                0 4px 8px rgba(0, 0, 0, 0.9)
              `,
              color: '#22c55e'
            }}
          >
            DEPLOYING TROOPS
          </h2>
        </div>

        {/* Source and Target Info */}
        <div 
          className="text-2xl font-bold uppercase tracking-wider"
          style={{
            fontFamily: "'Orbitron', sans-serif",
            textShadow: '0 2px 8px rgba(0,0,0,0.8)',
            color: '#fbbf24'
          }}
        >
          {sourceCountry} → {targetCountry}
        </div>

        {/* Military Icons Animation */}
        <div className="mt-8 flex justify-center gap-4">
          {[...Array(5)].map((_, i) => (
            <div
              key={`deploy-icon-${i}`}
              className="text-4xl animate-bounce"
              style={{
                animationDelay: `${i * 0.2}s`,
                filter: 'drop-shadow(0 0 10px rgba(34, 197, 94, 0.8))'
              }}
            >
              🪖
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
